<template>
    <ListFrame
        class="products-overview"
        :no-results="!resultExists"
        :loading="initialProductsLoading"
        :error="error"
        :total="total"
        large-content-container
    >
        <template #filter="{ isMobile }">
            <component
                :is="getListFilterComponent()"
                v-model:selected="selected"
                @update:selected="refetchList"
                :fetch-aggregation="fetchAggregation"
                :facet-collection-id="FacetCollection.productSelection"
                :is-mobile-view="isMobile"
                :more-menu-entries="$q.screen.gt.xs && !selectionData ? [] : menuEntries"
                :sort-order-entries="sortOrderItems"
                :initial-selected-sort-entry="sorting"
                @on-sort-selection-changed="onChangeSortOrder"
                @on-open-filter="onOpenFilterHandler"
                :sort-save-key="isSearch ? 'product_search_sort' : 'product_sort'"
            />
        </template>
        <template #content="{ isMobile }">
            <InfinityScroll
                :reach-bottom-text="t('product-and-asset.Loading {productsName}', { productsName: productName })"
                @load-more="onLoadMore"
                :reset-key="infiniteResetKey"
            >
                <TilesGrid
                    :is-mobile="isMobile"
                    :tiles="tiles"
                    :always-single-col="isMobile"
                    @select="emitSelect"
                    class="products-overview-tiles-grid"
                    :class="{ dense: dense }"
                />
            </InfinityScroll>
        </template>
        <template #no-results>
            <NoSearchResultsError :search-tab="productNamePlural" :class="{ 'q-mx-lg': $q.screen.lt.sm }" />
        </template>
    </ListFrame>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { computed, ref, watch } from "vue";
import { useProductsOverviewSort } from "./products-overview.model";
import { loadMore } from "@/shared/components/scroll/lazy-loading-fetch";
import { useI18n } from "vue-i18n";
import { Facet, FacetCollection, FacetSelected } from "@/shared/facets/facets.model";
import ListFrame from "@/shared/list-frame/list-frame.vue";
import TilesGrid from "@/shared/components/tiles/tiles-grid.vue";
import ListFilterRenderer from "@/shared/list-filter/list-filter-renderer.vue";
import ListFilterUrlSync from "@/shared/list-filter/list-filter-renderer-url-sync.vue";
import {
    QualificationLevelSelectionOptions,
    QualificationLevelSelectionResult,
} from "@/shared/environment/ability.types";
import { useProductDataSource } from "@/abilities/product-and-asset/pages/product-data-source";
import { useProductNames } from "@/abilities/product-and-asset/composables/product-names";
import { useHeaderMenuEntries } from "@/shared/search/header-menu-entries.ts";
import { CommonSearchTabAliases } from "@/shared/environment/common-route-aliases.ts";
import NoSearchResultsError from "@/shared/search/no-search-results-error.vue";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model.ts";
import { LAZY_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import InfinityScroll from "@/shared/components/scroll/infinity-scroll.vue";
import { useMatomo } from "@/shared/analytics/matomo.ts";

const props = withDefaults(
    defineProps<{
        fullTextSearchValue?: string;
        emitSelectionInTiles?: boolean;
        showButtons?: boolean;
        multiLevel?: boolean;
        hasLevelTitle?: boolean;
        selectionData?: QualificationLevelSelectionResult;
        selectionOptions?: QualificationLevelSelectionOptions;
        dense?: boolean;
        isSearch?: boolean;
    }>(),
    {
        fullTextSearchValue: "",
        showButtons: true,
        multiLevel: false,
        hasLevelTitle: false,
        dense: false,
        isSearch: false,
    }
);

const productName = useProductNames("pluralLower");
const productNamePlural = useProductNames("plural");
const { trackSearch } = useMatomo();

const emit = defineEmits<{
    select: [...params: any[]];
    onOpenNeedQualification: [];
}>();

const emitSelect = (...params: any) => {
    emit("select", ...params);
};

const selected = ref<FacetSelected[]>([]);
const facet = ref<Facet>();
const infiniteResetKey = ref<number>(0);

const getListFilterComponent = () => {
    return props.selectionData ? ListFilterRenderer : ListFilterUrlSync;
};

// Predefined list of product ids to select from
const productQualificationSelectedOptions = computed<string[] | undefined>(() => {
    return props.selectionOptions?.product;
});

const { t } = useI18n();
const { menuEntries } = useHeaderMenuEntries(computed(() => CommonSearchTabAliases.products));

const fullTextSearchValue = computed(() => {
    return props.fullTextSearchValue;
});

const resultExists = ref(true);

const { sortOrderItems, sorting } = useProductsOverviewSort(props.isSearch);

const { error, fetchMore, products, tiles, total, fetchAggregation, refetch } = useProductDataSource(
    computed(() => {
        return { text: fullTextSearchValue.value };
    }),
    props.emitSelectionInTiles ?? false,
    props.showButtons,
    selected,
    sorting,
    facet,
    productQualificationSelectedOptions
);

const initialProductsLoading = computed(() => {
    return !products.value;
});

watch(products, () => {
    if (!products.value) {
        return;
    }
    resultExists.value = products.value?.products.length > 0;
    trackSearch(fullTextSearchValue.value, "products", total.value.count);
});

const onOpenFilterHandler = (facetProp: Facet) => {
    facet.value = facetProp;
};

// We need to count up `infiniteResetKey` after a refetch so that `InfinityScroll` (and
// internally Quasar's `QInfiniteScroll`) resets its internal loading state.
// Without this, changing filters or sort could leave the infinite scroll in a
// "done" state, and it would not call on load more anymore.
const refetchList = async () => {
    await refetch();
    infiniteResetKey.value += 1;
};

const onChangeSortOrder = async (selectedItem: SortEntry) => {
    sorting.value = { field: selectedItem.field, order: selectedItem.order };
    await refetchList();
};

const onLoadMore = async (done: (stop: boolean) => void) => {
    if (!products.value?.products?.length || products.value.products.length === total.value.count) {
        done(true);
        return;
    }

    const lastProduct = products.value.products[products.value.products.length - 1];
    if (!lastProduct.cursor) {
        console.error("No cursor found for last product, cannot load more.", lastProduct);
        done(true);
        return;
    }

    try {
        const result = await loadMore(fetchMore, lastProduct.cursor, LAZY_LOAD_AMOUNT);
        let loadMoreLastCursor = undefined;
        if (result?.data?.products?.products && result.data.products.products.length > 0) {
            loadMoreLastCursor = result.data.products.products[result.data.products.products.length - 1].cursor;
        }

        const stop = loadMoreLastCursor ? lastProduct.cursor === loadMoreLastCursor : true;
        done(stop);
    } catch (e) {
        console.error(e);
        done(true);
    }
};
</script>

<style lang="scss" scoped>
.products-overview-tiles-grid:not(.dense) {
    padding: 0;
}
.tile-grid {
    gap: 12px;
}
:deep(.slot-content) {
    display: grid;
}
</style>
